import React, { useEffect, useState } from 'react';

import classNames from 'classnames';
import debounce from 'lodash.debounce';

import { partnerLogoMap as PARTNER_MAP } from 'wanderu-assets';
import Intercom from 'wanderu-component-intercom';
import PartnerLogo from 'wanderu-component-partner-logo';
import {
    Layout,
    Logo,
} from 'wanderu-components';

import AccountMenu from './AccountMenu/AccountMenu';
import CurrencyMenu from './CurrencyMenu/CurrencyMenu';
import Hamburger from './Hamburger/Hamburger';
import LocaleMenu from './LocaleMenu/LocaleMenu';
import LocaleSwitchPrompt from './LocaleSwitchPrompt/LocaleSwitchPrompt';
import OffCanvasMenu from './OffCanvasMenu/OffCanvasMenu';
import SiteMenu from './SiteMenu/SiteMenu';

import styles from './Header.scss';

const MOBILE_BREAKPOINT = 999;
const VERTICAL_DOWN_SCROLL_TRIGGER = 510;
const VERTICAL_UP_SCROLL_TRIGGER = 430;
const COBRANDED_PAGES = /depart|return|tripsummary/;

const renderHeaderSpacer = () => (
    <div className={styles.headerSpacer} />
);

interface HeaderProps {
    affiliateId?: string,
    currency?: string,
    enableLocaleSwitcher?: boolean,
    globalBannerVisible?: boolean,
    ipLocation?: object,
    isExternal?: boolean,
    isLandingPage?: boolean,
    isLoggedIn?: boolean,
    isSticky?: boolean,
    language?: string,
    locale?: string,
    localeSwitcherVisible?: boolean,
    native?: boolean,
    navLinksVisible?: boolean,
    onCurrencyChange: () => void,
    onLocaleChange: () => void,
    onLogOut: () => void,
    onMenuToggle: () => void,
    path?: string,
    renderOffCanvasMenu?: boolean,
    rootUrl?: string,
    showDropdown?: boolean,
    supportedCurrencies?: string[],
    supportedLocales?: string[],
    userFirstName?: string,
}

function Header(props: HeaderProps) {
    const [isOffCanvasMenuOpen, setIsOffCanvasMenuOpen] = useState(false);
    const [showSticky, setShowSticky] = useState(false);

    const {
        affiliateId = '',
        currency = '',
        enableLocaleSwitcher = false,
        globalBannerVisible = false,
        ipLocation,
        isExternal = false,
        isLandingPage = false,
        isLoggedIn = false,
        isSticky,
        language = '',
        locale = '',
        localeSwitcherVisible = false,
        native = true,
        navLinksVisible = false,
        onCurrencyChange,
        onLocaleChange,
        onLogOut = () => undefined,
        onMenuToggle = () => undefined,
        path = '',
        renderOffCanvasMenu: shouldRenderOffCanvasMenu = true,
        rootUrl: baseUrl = '',
        showDropdown = true,
        supportedCurrencies = [],
        supportedLocales = [],
        userFirstName = '',
    } = props;

    const onResize = () => {
        const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;

        if (isMobile) {
            return;
        }

        onMenuToggle();
        window.removeEventListener('resize', debouncedOnResize);

        setIsOffCanvasMenuOpen(false);
    };

    const debouncedOnResize = debounce(onResize, 100);

    useEffect(() => {
        return () => window.removeEventListener('resize', debouncedOnResize);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const detectScroll = () => {
            const downTrigger = VERTICAL_DOWN_SCROLL_TRIGGER;
            const upTrigger = VERTICAL_UP_SCROLL_TRIGGER;

            if (window.scrollY > downTrigger && !showSticky) {
                return setShowSticky(true);
            } if (window.scrollY <= upTrigger && showSticky) {
                return setShowSticky(false);
            }

            return undefined;
        };

        if (isSticky) {
            window.addEventListener('scroll', detectScroll);
        }

        return () => {
            if (isSticky) {
                window.removeEventListener('scroll', detectScroll);
            }
        };
    }, [showSticky, setShowSticky, isSticky]);

    const onToggleMobileMenu = () => {
        if (!isOffCanvasMenuOpen) {
            window.addEventListener('resize', debouncedOnResize);
        } else {
            window.removeEventListener('resize', debouncedOnResize);
        }

        onMenuToggle();
        setIsOffCanvasMenuOpen(!isOffCanvasMenuOpen);
    };

    const renderDesktopMenus = () => (
        <div className={styles.accountLocaleContainer}>
            <div className={styles.menu}>
                {
                    showDropdown && (
                        <LocaleMenu
                            currentLocale={locale}
                            onLocaleChange={onLocaleChange}
                            path={path}
                            supportedLocales={supportedLocales}
                        />
                    )
                }
            </div>
            <div className={styles.menu}>
                <CurrencyMenu
                    currentCurrency={currency}
                    currentLocale={locale}
                    onCurrencyChange={onCurrencyChange}
                    supportedCurrencies={supportedCurrencies}
                />
            </div>
            <div
                className={classNames(
                    styles.menu,
                    styles.accountMenu,
                )}
            >
                <AccountMenu
                    isLoggedIn={isLoggedIn}
                    onLogOut={onLogOut}
                    userFirstName={userFirstName}
                />
            </div>
            {
                enableLocaleSwitcher && showDropdown && (
                    <LocaleSwitchPrompt
                        currentLocale={locale}
                        ipLocation={ipLocation}
                        onLocaleChange={onLocaleChange}
                    />
                )

            }
        </div>
    );

    const renderHamburger = () => (
        <Hamburger
            className={styles.hamburger}
            isActive={isOffCanvasMenuOpen}
            onClick={onToggleMobileMenu}
        />
    );

    const renderHeader = () => {
        const partner = PARTNER_MAP[affiliateId as keyof typeof PARTNER_MAP];
        const showPartnerLogo = path.match(COBRANDED_PAGES) && !!partner;
        const rootUrlEnd = baseUrl[baseUrl.length - 1] === '/'
            ? ''
            : '/';
        const rootUrl = baseUrl
            ? `${baseUrl}${rootUrlEnd}`
            : '';

        return (
            <header className={styles.navBar}>
                <div className={styles.logoAndLinkContainer}>
                    <Logo
                        className={styles.logo}
                        isExternal={isExternal}
                        native={native || !!rootUrl}
                        link
                    />

                    {!showPartnerLogo && navLinksVisible && (
                        <SiteMenu
                            className={styles.siteMenu}
                            isExternal={isExternal}
                            isLandingPage={isLandingPage}
                            language={language}
                            locale={locale}
                            rootUrl={rootUrl}
                        />
                    )}

                    {showPartnerLogo && (
                        <PartnerLogo
                            partner={partner}
                            partnerId={affiliateId}
                        />
                    )}
                </div>

                {localeSwitcherVisible && renderDesktopMenus()}
                {localeSwitcherVisible && renderHamburger()}
            </header>
        );
    };

    const renderOffCanvasMenu = () => (
        <>
            <OffCanvasMenu
                globalBannerVisible={globalBannerVisible}
                isOpen={isOffCanvasMenuOpen}
                onCloseMenu={onToggleMobileMenu}
            >
                {renderHeaderSpacer()}

                <AccountMenu
                    isLoggedIn={isLoggedIn}
                    onLogOut={onLogOut}
                    onMenuToggle={onToggleMobileMenu}
                    userFirstName={userFirstName}
                    vertical
                />

                <SiteMenu
                    isLandingPage={isLandingPage}
                    language={language}
                    locale={locale}
                    onCloseMenu={onToggleMobileMenu}
                    rootUrl={baseUrl}
                    vertical
                />

                <LocaleMenu
                    currentLocale={locale}
                    onLocaleChange={onLocaleChange}
                    path={path}
                    supportedLocales={supportedLocales}
                    vertical
                />

                <CurrencyMenu
                    currentCurrency={currency}
                    currentLocale={locale}
                    onCurrencyChange={onCurrencyChange}
                    supportedCurrencies={supportedCurrencies}
                    vertical
                />

                <Intercom wrapperClassName={styles.mobileIntercomWrapper} />
            </OffCanvasMenu>

            {isOffCanvasMenuOpen && renderHeaderSpacer()}
        </>
    );

    return (
        <div
            className={classNames(
                styles.container,
                {
                    [styles.sticky]: showSticky,
                },
            )}
            data-id="site-header"
            data-testid="header"
        >
            <Layout
                className={classNames(
                    styles.main,
                    styles.layout,

                    {
                        [styles.open]: isOffCanvasMenuOpen,
                    },
                )}
                data-testid="header-layout"
            >
                {renderHeader()}
            </Layout>

            {shouldRenderOffCanvasMenu && renderOffCanvasMenu()}
        </div>
    );
}

export { MOBILE_BREAKPOINT };
export default Header;
